// ============================================================
//  API Service — pointed at Spring Boot backend
//  Base URL: http://localhost:8080/api/v1
// ============================================================

import axios from "axios";

// ✅ Change this to your deployed backend URL in production
const BASE_URL = "https://learnwithpayal-backend-production.up.railway.app/api/v1";

const api = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Auto-attach JWT token on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("edu_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Unwrap response data; handle 401 by clearing session
api.interceptors.response.use(
  (res) => res.data,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("edu_token");
      localStorage.removeItem("edu_user");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

// ── Boards ────────────────────────────────────────────────
// GET  /boards                    → [{ id, name, slug }]
export const getBoards = () => api.get("/boards");

// ── Classes ───────────────────────────────────────────────
// GET  /boards/:boardId/classes   → [{ id, name, number, boardId, boardName }]
export const getClasses = (boardId) => api.get(`/boards/${boardId}/classes`);

// ── Subjects ──────────────────────────────────────────────
// GET  /boards/:boardId/classes/:classId/subjects → [{ id, name, icon, boardId, classId }]
export const getSubjects = (boardId, classId) =>
  api.get(`/boards/${boardId}/classes/${classId}/subjects`);

// ── Notes ─────────────────────────────────────────────────
// GET  /subjects/:subjectId/notes → [{ id, title, description, fileUrl, fileSize, uploadedAt }]
export const getNotes = (subjectId) => api.get(`/subjects/${subjectId}/notes`);

// GET  /notes/:noteId/download    → PDF blob
export const downloadNote = (noteId) =>
  api.get(`/notes/${noteId}/download`, { responseType: "blob" });

// ── Videos ────────────────────────────────────────────────
// GET  /subjects/:subjectId/videos → [{ id, title, youtubeId, duration, chapter }]
export const getVideos = (subjectId) => api.get(`/subjects/${subjectId}/videos`);

// ── Search ────────────────────────────────────────────────
// GET  /search?q=&boardId=&classId= → { notes: [], videos: [] }
export const searchContent = (query, boardId, classId) =>
  api.get("/search", { params: { q: query, boardId, classId } });

// ── Auth ──────────────────────────────────────────────────
// POST /auth/login   Body: { email, password } → { token, user: { id, name, email, role } }
export const login = (credentials) => api.post("/auth/login", credentials);
// POST /auth/logout
export const logout = () => api.post("/auth/logout");

// ── Teacher: Stats ────────────────────────────────────────
// GET  /teacher/stats → { totalNotes, totalVideos, totalBoards, totalClasses, totalSubjects }
export const getTeacherStats = () => api.get("/teacher/stats");

// ── Teacher: Board CRUD ───────────────────────────────────
// POST   /teacher/boards          Body: { name, slug }
export const createBoard = (data) => api.post("/teacher/boards", data);
// PUT    /teacher/boards/:id      Body: { name, slug }
export const updateBoard = (id, data) => api.put(`/teacher/boards/${id}`, data);
// DELETE /teacher/boards/:id
export const deleteBoard = (id) => api.delete(`/teacher/boards/${id}`);

// ── Teacher: Class CRUD ───────────────────────────────────
// POST   /teacher/classes         Body: { name, number, boardId }
export const createClass = (data) => api.post("/teacher/classes", data);
// PUT    /teacher/classes/:id     Body: { name, number, boardId }
export const updateClass = (id, data) => api.put(`/teacher/classes/${id}`, data);
// DELETE /teacher/classes/:id
export const deleteClassItem = (id) => api.delete(`/teacher/classes/${id}`);

// ── Teacher: Subject CRUD ─────────────────────────────────
// POST   /teacher/subjects        Body: { name, icon, boardId, classId }
export const createSubject = (data) => api.post("/teacher/subjects", data);
// PUT    /teacher/subjects/:id    Body: { name, icon }
export const updateSubject = (id, data) => api.put(`/teacher/subjects/${id}`, data);
// DELETE /teacher/subjects/:id
export const deleteSubject = (id) => api.delete(`/teacher/subjects/${id}`);

// ── Teacher: Note CRUD ────────────────────────────────────
// POST   /teacher/notes  (multipart/form-data) fields: title, description, subjectId, file
export const uploadNote = (formData) =>
  api.post("/teacher/notes", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
// PUT    /teacher/notes/:id       Body: { title, description, subjectId }
export const updateNoteApi = (id, data) => api.put(`/teacher/notes/${id}`, data);
// DELETE /teacher/notes/:id
export const deleteNote = (noteId) => api.delete(`/teacher/notes/${noteId}`);

// ── Teacher: Video CRUD ───────────────────────────────────
// POST   /teacher/videos          Body: { title, youtubeId, chapter, duration, subjectId }
export const uploadVideo = (data) => api.post("/teacher/videos", data);
// PUT    /teacher/videos/:id      Body: { title, youtubeId, chapter, duration, subjectId }
export const updateVideoApi = (id, data) => api.put(`/teacher/videos/${id}`, data);
// DELETE /teacher/videos/:id
export const deleteVideo = (videoId) => api.delete(`/teacher/videos/${videoId}`);

export default api;
